import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Spinner, Alert, Form, Row, Col, ListGroup } from 'react-bootstrap';
import axios from 'axios';
import { Location, QueryParams } from '../interfaces';
import MessageModal from './MessageModal';
import ConfirmationModal from './ConfirmationModal';
import LocationForm from './LocationForm';
// אייקונים
import { 
  faPlus, 
  faEdit, 
  faTrash 
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL, buildApiUrl } from '../config/api';

const LocationList: React.FC = () => {
  const { user } = useAuth();
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState<boolean>(false);
  const [locationToEdit, setLocationToEdit] = useState<Location | null>(null);
  
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  const [showMessageModal, setShowMessageModal] = useState<boolean>(false);
  const [messageModalTitle, setMessageModalTitle] = useState<string>('');
  const [messageModalContent, setMessageModalContent] = useState<string>('');
  const [messageModalVariant, setMessageModalVariant] = useState<'success' | 'danger' | 'info' | 'warning'>('info');

  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState<string>('');
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmModalTitle, setConfirmModalTitle] = useState<string>('');

  const fetchLocations = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: QueryParams = {};
      if (searchQuery) params.search = searchQuery;

      const response = await axios.get(buildApiUrl('settings/locations/'), { params });
      setLocations(response.data);
    } catch (err) {
      console.error("שגיאה בטעינת מיקומים:", err);
      setError("לא ניתן לטעון מיקומים.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchLocations();
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const showMessage = (title: string, message: string, variant: 'success' | 'danger' | 'info' | 'warning' = 'info') => {
    setMessageModalTitle(title);
    setMessageModalContent(message);
    setMessageModalVariant(variant);
    setShowMessageModal(true);
  };

  const handleAddClick = () => {
    setLocationToEdit(null);
    setShowForm(true);
  };
  
  const handleEditClick = (location: Location) => {
    setLocationToEdit(location);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    fetchLocations();
    showMessage('הצלחה', locationToEdit ? `המיקום "${locationToEdit.location_name}" עודכן בהצלחה!` : 'מיקום חדש נוצר בהצלחה!', 'success');
  };
  
  const handleFormCancel = () => {
    setShowForm(false);
    setLocationToEdit(null);
  };

  const handleDeleteClick = (id: number) => {
    setConfirmModalTitle('אישור מחיקה');
    setConfirmModalMessage('האם אתה בטוח שברצונך למחוק מיקום זה? פעולה זו בלתי הפיכה.');
    setConfirmAction(() => async () => {
      try {
        const loc = locations.find(l => l.id === id);
        await axios.delete(`${buildApiUrl('settings/locations/')}id}/`);
        showMessage('הצלחה', `המיקום ${loc ? `"${loc.location_name}" ` : ''}נמחק בהצלחה!`, 'success');
        fetchLocations();
      } catch (err) {
        showMessage('שגיאה', 'שגיאה במחיקת המיקום.', 'danger');
      }
    });
    setShowConfirmModal(true);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 app-sticky-page-toolbar">
        <h2>ניהול מיקומים</h2>
        <Button variant="success" onClick={handleAddClick}>
          <FontAwesomeIcon icon={faPlus} className="me-1" />
          הוסף מיקום חדש
        </Button>
      </div>

      <Form className="mb-4 p-3 border rounded bg-light">
        <Row>
          <Col md={12}>
            <Form.Group controlId="searchQuery">
              <Form.Label>חיפוש</Form.Label>
              <Form.Control type="text" placeholder="חפש לפי שם, כתובת, הערות..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </Form.Group>
          </Col>
        </Row>
      </Form>

      {loading && <Spinner animation="border" />}
      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && !error && (
        !locations || locations.length === 0 ? (
          <p>אין מיקומים במערכת.</p>
        ) : (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>שם מיקום</th>
                <th>סוג מיקום</th>
                <th>כתובת</th> {/* נקודה 9 */}
                <th>הערות</th> {/* נקודה 9 */}
                <th>ציוד מומלץ</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {locations.map(location => (
                <tr key={location.id}>
                  <td>{location.location_name}</td>
                  <td>
                    {location.location_type ? (
                      <span className={`badge ${location.location_type.includes('פנים') ? 'bg-success' : 'bg-warning'}`}>
                        {location.location_type.includes('פנים') ? '🏠 פנים' : '🌳 חוץ'}
                      </span>
                    ) : 'N/A'}
                  </td>
                  <td>{location.address || 'N/A'}</td> {/* נקודה 9 */}
                  <td>{location.notes || 'N/A'}</td> {/* נקודה 9 */}
                  <td>
                    {location.recommended_equipment_details && location.recommended_equipment_details.length > 0 ? (
                      <ListGroup variant="flush">
                        {location.recommended_equipment_details.map(item => (
                          <ListGroup.Item key={item.id}>
                            {item.quantity}x {item.equipment_name}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    ) : 'אין'}
                  </td>
                  <td>
                                                    <Button variant="warning" size="sm" className="me-2" onClick={() => handleEditClick(location)}>
                                  <FontAwesomeIcon icon={faEdit} className="me-1" />
                                  ערוך
                                </Button>
                    <Button variant="danger" size="sm" onClick={() => handleDeleteClick(location.id!)} disabled={user?.role?.name === 'WAREHOUSE'} title={user?.role?.name === 'WAREHOUSE' ? 'למחסנאי אין הרשאה למחיקה' : ''}>
                                  <FontAwesomeIcon icon={faTrash} className="me-1" />
                                  מחק
                                </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )
      )}

      <Modal show={showForm} onHide={handleFormCancel} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{locationToEdit ? 'עריכת מיקום' : 'הוספת מיקום חדש'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <LocationForm
            locationToEdit={locationToEdit}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        </Modal.Body>
      </Modal>

      <MessageModal show={showMessageModal} onHide={() => setShowMessageModal(false)} title={messageModalTitle} message={messageModalContent} variant={messageModalVariant} />
      <ConfirmationModal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} title={confirmModalTitle} message={confirmModalMessage} onConfirm={confirmAction || (() => {})} />
    </div>
  );
};

export default LocationList;
